/**
 * Composite progress and submission, delegated child by child through the registry.
 *
 * This type consumes other types exactly as the core does — it is the check that the plugin
 * interface is complete (CLAUDE.md §7.6).
 */
import { getPuzzleType } from "../registry";
import type { CompositeDefinition, CompositeProgress, CompositeSubmission } from "./types";

export function emptyDefinition(): CompositeDefinition {
  return {
    puzzleType: "composite",
    typeVersion: 1,
    mode: "free",
    children: [],
    rulesText: { fr: "", en: "" },
  };
}

export function emptyProgress(definition: CompositeDefinition): CompositeProgress {
  return {
    version: 1,
    children: (definition.children ?? []).map((child) => {
      const module = getPuzzleType(child.puzzleType);
      return module ? module.emptyProgress(child.definition) : null;
    }),
  };
}

/**
 * Tolerates a wrong-length or garbage array: each slot is migrated by its own child type, and
 * a missing slot falls back to that child's empty progress.
 */
export function migrateProgress(definition: CompositeDefinition, raw: unknown): CompositeProgress {
  const stored =
    typeof raw === "object" &&
    raw !== null &&
    Array.isArray((raw as { children?: unknown }).children)
      ? ((raw as { children: unknown[] }).children as unknown[])
      : [];
  return {
    version: 1,
    children: (definition.children ?? []).map((child, index) => {
      const module = getPuzzleType(child.puzzleType);
      if (!module) return stored[index] ?? null;
      return module.migrateProgress
        ? module.migrateProgress(child.definition, stored[index])
        : (stored[index] ?? module.emptyProgress(child.definition));
    }),
  };
}

export function serializeSubmission(
  definition: CompositeDefinition,
  progress: CompositeProgress,
): CompositeSubmission {
  return {
    children: (definition.children ?? []).map((child, index) => {
      const module = getPuzzleType(child.puzzleType);
      if (!module) return {};
      const childProgress = progress.children[index] ?? module.emptyProgress(child.definition);
      return module.serializeSubmission(child.definition, childProgress);
    }),
  };
}

/** Replaces one child's progress slot without disturbing the others. */
export function withChildProgress(
  progress: CompositeProgress,
  index: number,
  next: unknown,
): CompositeProgress {
  const children = [...progress.children];
  children[index] = next;
  return { version: 1, children };
}
